import { LucideIcon } from "lucide-react";
import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from "shared/components/ui/card";

import { CardColorScheme, getCardColorScheme } from "@/lib/constants/colors";

interface SkeletonInfoCardProps {
  title: string;
  subtitle: string;
  colorScheme?: CardColorScheme;
  icon?: LucideIcon;
  iconSize?: number;
}

const SkeletonInfoCard: React.FC<SkeletonInfoCardProps> = ({
  title,
  subtitle,
  colorScheme = "scout-blue",
  icon: Icon,
  iconSize = 5,
}) => {
  // Get color scheme from our shared constants
  const color = getCardColorScheme(colorScheme);

  return (
    <Card className={`${color.border} shadow-sm overflow-hidden`}>
      <div
        className={`bg-gradient-to-r ${color.gradientFrom} ${color.gradientTo} border-b ${color.borderB}`}
      >
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className={color.title}>{title}</CardTitle>
            {Icon && (
              <Icon className={`h-${iconSize} w-${iconSize} ${color.icon}`} />
            )}
          </div>
          <CardDescription>{subtitle}</CardDescription>
        </CardHeader>
      </div>
      <CardContent className="pt-6">
        <div className="space-y-4">
          {/* Date & Time Section */}
          <div className="flex items-start space-x-3">
            <div
              className={`h-5 w-5 ${color.iconColor} mt-1 bg-gray-300 rounded animate-pulse`}
            />
            <div className="flex-1">
              <p className={`text-sm font-medium ${color.labelText}`}>
                DATE & TIME
              </p>
              <div className="space-y-1">
                <div className="h-4 bg-gray-300 rounded animate-pulse w-32"></div>
                <div className="h-4 bg-gray-300 rounded animate-pulse w-24"></div>
              </div>
            </div>
          </div>

          {/* Location Section */}
          <div className="flex items-start space-x-3">
            <div
              className={`h-5 w-5 ${color.iconColor} mt-1 bg-gray-300 rounded animate-pulse`}
            />
            <div className="flex-1">
              <p className={`text-sm font-medium ${color.labelText}`}>
                LOCATION
              </p>
              <div className="h-4 bg-gray-300 rounded animate-pulse w-48"></div>
            </div>
          </div>

          {/* Description Section */}
          <div className="space-y-2">
            <div className="h-4 bg-gray-300 rounded animate-pulse w-full"></div>
            <div className="h-4 bg-gray-300 rounded animate-pulse w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded animate-pulse w-1/2"></div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SkeletonInfoCard;
